import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ZoomIn, ZoomOut, Maximize, RotateCcw, X, ChevronLeft, ChevronRight } from 'lucide-react';

export function ImageZoomModal({ 
  isOpen, 
  onClose, 
  imageUrl, 
  title = "Bild Vorschau",
  // Multi-page support
  thumbnails = null,
  totalPages = 1,
  currentPage = 1,
  onPageChange = null
}) {
  const [zoomLevel, setZoomLevel] = useState(100);
  const [fitToScreen, setFitToScreen] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  // Reset state when modal opens (but preserve zoom settings when changing pages)
  useEffect(() => {
    if (isOpen) {
      // Use a timeout to avoid synchronous setState in effect
      const timer = setTimeout(() => {
        setZoomLevel(100);
        setFitToScreen(true);
        setImageLoaded(false);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle page changes - preserve zoom settings
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        setImageLoaded(false);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, currentPage, imageUrl]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen || !onPageChange || totalPages <= 1) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && currentPage > 1) {
        e.preventDefault();
        onPageChange(currentPage - 1);
      } else if (e.key === 'ArrowRight' && currentPage < totalPages) {
        e.preventDefault();
        onPageChange(currentPage + 1);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentPage, totalPages, onPageChange]);

  // Handle image load
  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  // Zoom functions - enhanced zoom levels for better document reading
  const handleZoomIn = () => {
    setFitToScreen(false);
    setZoomLevel(prev => Math.min(prev + 25, 300)); // Increased max zoom to 300%
  };

  const handleZoomOut = () => {
    setFitToScreen(false);
    setZoomLevel(prev => Math.max(prev - 25, 25)); // Decreased min zoom to 25%
  };

  const handleOriginalSize = () => {
    setFitToScreen(false);
    setZoomLevel(100);
  };

  const handleFitToScreen = () => {
    setFitToScreen(true);
    setZoomLevel(100);
  };

  const handleReset = () => {
    setFitToScreen(true);
    setZoomLevel(100);
  };

  // Multi-page navigation
  const handlePreviousPage = () => {
    if (onPageChange && currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (onPageChange && currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const getCurrentImageUrl = () => {
    if (thumbnails && thumbnails.length > 0) {
      const thumbnail = thumbnails.find(t => t.page === currentPage);
      // P0 FIX: Ensure zoom modal uses high-res zoom_image_base64, fallback to thumbnail
      const zoomImage = thumbnail?.zoom_image_base64 || thumbnail?.thumbnail_base64 || imageUrl;
      
      // P0 FIX: Debug logging for zoom image usage (DEV only)
      if (process.env.NODE_ENV === 'development') {
        console.log('[ImageZoomModal] getCurrentImageUrl - currentPage:', currentPage);
        console.log('[ImageZoomModal] Using zoom_image_base64:', !!thumbnail?.zoom_image_base64);
        console.log('[ImageZoomModal] Fallback to thumbnail_base64:', !!thumbnail?.thumbnail_base64);
        console.log('[ImageZoomModal] Final fallback to imageUrl:', !!imageUrl);
      }
      
      return zoomImage;
    }
    return imageUrl;
  };

  // Get image style based on current settings
  const getImageStyle = () => {
    if (fitToScreen) {
      return {
        maxWidth: '100%',
        maxHeight: '70vh',
        width: 'auto',
        height: 'auto',
        objectFit: 'contain'
      };
    } else {
      return {
        width: `${zoomLevel}%`,
        height: 'auto',
        maxWidth: 'none',
        maxHeight: 'none'
      };
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] p-0">
        <DialogHeader className="p-4 pb-2">
          <div className="flex items-center gap-4">
            <DialogTitle className="text-lg font-semibold">{title}</DialogTitle>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreviousPage}
                  disabled={currentPage <= 1}
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-gray-600 min-w-[5rem] text-center">
                  Seite {currentPage} von {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={currentPage >= totalPages}
                  className="h-8 w-8 p-0"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </DialogHeader>
        
        {/* Zoom Controls */}
        <div className="px-4 pb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant={fitToScreen ? "default" : "outline"}
              size="sm"
              onClick={handleFitToScreen}
              className="flex items-center gap-1"
            >
              <Maximize className="h-3 w-3" />
              An Bildschirm anpassen
            </Button>
            
            <Button
              variant={!fitToScreen && zoomLevel === 100 ? "default" : "outline"}
              size="sm"
              onClick={handleOriginalSize}
              className="flex items-center gap-1"
            >
              Originalgröße (100%)
            </Button>
            
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={handleZoomOut}
                disabled={zoomLevel <= 25}
                className="h-8 w-8 p-0"
              >
                <ZoomOut className="h-3 w-3" />
              </Button>
              
              <span className="text-sm font-medium min-w-[3rem] text-center">
                {fitToScreen ? 'Auto' : `${zoomLevel}%`}
              </span>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleZoomIn}
                disabled={zoomLevel >= 300}
                className="h-8 w-8 p-0"
              >
                <ZoomIn className="h-3 w-3" />
              </Button>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              className="flex items-center gap-1"
            >
              <RotateCcw className="h-3 w-3" />
              Zurücksetzen
            </Button>
          </div>
        </div>
        
        {/* Image Container */}
        <div 
          ref={containerRef}
          className="flex-1 overflow-auto p-4 pt-0"
          style={{ 
            minHeight: '400px',
            maxHeight: 'calc(95vh - 120px)'
          }}
        >
          <div className="flex justify-center items-start min-h-full">
            {getCurrentImageUrl() && (
              <img
                key={`zoom-page-${currentPage}`} // P0 FIX: Force re-render when page changes
                ref={imageRef}
                src={getCurrentImageUrl()}
                alt={totalPages > 1 ? `${title} - Seite ${currentPage}` : title}
                onLoad={handleImageLoad}
                style={{
                  ...getImageStyle(),
                  imageRendering: 'crisp-edges', // Better rendering for documents
                  WebkitImageRendering: '-webkit-optimize-contrast', // Safari optimization
                }}
                className={`transition-all duration-200 ${
                  !imageLoaded ? 'opacity-0' : 'opacity-100'
                } ${
                  !fitToScreen && zoomLevel > 100 ? 'cursor-move' : 'cursor-default'
                }`}
                draggable={false}
              />
            )}
            
            {!imageLoaded && (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            )}
          </div>
        </div>
        
        {/* Status Bar */}
        <div className="px-4 py-2 bg-gray-50 border-t text-xs text-gray-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span>
                Zoom: {fitToScreen ? 'An Bildschirm angepasst' : `${zoomLevel}%`}
              </span>
              {totalPages > 1 && (
                <span>
                  Seite {currentPage} von {totalPages}
                </span>
              )}
            </div>
            <span className="text-gray-500">
              {totalPages > 1 
                ? 'Tipp: ← → Pfeiltasten für Navigation • Zoom-Buttons für Lesbarkeit'
                : 'Tipp: Verwenden Sie die Zoom-Buttons für bessere Lesbarkeit'
              }
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ImageZoomModal;